import { Banner } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';
import { ChatWidgetTroubleshootingTool } from '@mm/stateful-components';
import { CustomerChatConditionRulesEditor } from '@mm/stateful-components/CustomerChat/CustomerChatConditionRulesEditor/CustomerChatConditionRulesEditor';
import { useCustomerChatConditionsEditorPromoterState } from '@mm/stateful-components/CustomerChat/use-customer-chat-conditions-editor-state';
import { CustomerChatInstallInformation } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatInstallInformation/CustomerChatInstallInformation';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './GetemailsSlideoutEditor.module.css';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { GetemailsSetupSlideout } from '../GetemailsSetupSlideout/GetemailsSetupSlideout';
import { chatWidgetTroubleshootingLastResultSelector } from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { XRaySlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export type GetemailsSlideoutEditorProps = {
  onUnmount: () => {};
  promoterId: number;
};
export const GetemailsSlideoutEditorSettings = ({
  promoterId,
}: GetemailsSlideoutEditorProps) => {
  const hasXRaySubscriptionFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).xray;
  const chatbotId = useSelector(activeChatbotIdSelector);
  const promoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );
  const canEditConditions = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditConditions;
  const promoter = promoters.find(p => p.id === promoterId);
  const promoterCRUDActions = usePromoterCRUDActions();
  const [rulesExpanded, setRulesExpanded] = useState(true);
  const [installInfoExpanded, setInstallInfoExpanded] = useState(true);
  const { setRef } = useSlideoutOnClose();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const { pixelName: whitelabelPixelName } = useWhitelabelConfig();
  const pixelName = whitelabelPixelName
    ? `${whitelabelPixelName} Lead Generator`
    : 'X-Ray Lead Generation Pixel';

  const {
    localPromoter,
    setLocalPromoter,
    updatePromoter,
    updateLocalPromoterField,
  } = useCustomerChatConditionsEditorPromoterState(promoter);

  const promoterHasNoConditions = (promoter: PromoterModel) =>
    !promoter.conditions?.rules.length ||
    promoter.conditions.rules.every(r => r.operator === '*');

  const warnAboutMultipleRuleFreePromoters =
    promoter &&
    promoterHasNoConditions(promoter) &&
    promoters.some(p => p.id !== promoter.id && promoterHasNoConditions(p));

  const duplicateRules =
    !warnAboutMultipleRuleFreePromoters && promoter?.conditions?.rules.length
      ? promoter.conditions.rules.filter(rule =>
          promoters.some(
            p =>
              p.id !== promoter.id &&
              (p.conditions?.rules || []).some(
                otherRule =>
                  otherRule.operator === rule.operator &&
                  otherRule.value === rule.value,
              ),
          ),
        )
      : [];

  const updatePromoterStatus = async (active: boolean) => {
    await promoterCRUDActions.update(chatbotId, {
      id: promoter?.id,
      active,
    });
  };

  const customActivateAction = async () => {
    if (!hasXRaySubscriptionFlag) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['STARTBOTS-X_RAY_ACTIVATION-EDITOR'],
        feature: 'xray',
        onSuccessBilling: () => updatePromoterStatus(true),
      });
    }

    if (hasXRaySubscriptionFlag && !promoter?.active) {
      updatePromoterStatus(true);
    } else {
      updatePromoterStatus(false);
    }
  };

  useEffect(() => {
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(chatbotId, 'getemails');
    }

    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        className={styles.header}
        promoter={promoter}
        title={pixelName}
        customActivateAction={customActivateAction}
        deleteWithPromoterCRUDAction
      />
      <SlideoutMenu.Content className={styles.content}>
        <Banner
          css={{
            container: styles.bannerContainer,
            header: styles.bannerHeader,
            collapsedHeader: styles.collapsedHeader,
            content: styles.bannerContent,
          }}
          withBannerHeader
          headerText="What is Email Address of Visitor Detected?"
          withLeftArea={false}
          showCloseIcon
          usedInSlideout
        >
          <XRaySlideoutBanner textColor="white" />
        </Banner>
        <CollapsibleContentContainerWithoutLocalState
          orderNumber="1"
          containerName="Specify what pages on your Website should trigger this automation"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="webchat"
          useCircleNumberStyle
          isExpanded={rulesExpanded}
          toggleIsExpanded={setRulesExpanded}
        >
          <CustomerChatConditionRulesEditor
            usedInSlideout
            updateLocalPromoterField={updateLocalPromoterField}
            updatePromoter={updatePromoter}
            localPromoter={localPromoter}
            setLocalPromoter={setLocalPromoter}
            canEditConditions={canEditConditions}
            warnAboutMultipleRuleFreePromoters={
              warnAboutMultipleRuleFreePromoters
            }
            duplicateRules={duplicateRules}
          />
        </CollapsibleContentContainerWithoutLocalState>

        <CollapsibleContentContainerWithoutLocalState
          orderNumber="2"
          containerName="Install Website Script"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="webchat"
          useCircleNumberStyle
          isExpanded={installInfoExpanded}
          toggleIsExpanded={setInstallInfoExpanded}
        >
          <CustomerChatInstallInformation
            promoter={promoter}
            usedInSlideout
            hideWhitelist
          />
        </CollapsibleContentContainerWithoutLocalState>
        <CollapsibleContentContainerWithoutLocalState
          orderNumber="3"
          containerName="Verify the script is working properly"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="webchat"
          useCircleNumberStyle
          isExpanded={installInfoExpanded}
          toggleIsExpanded={setInstallInfoExpanded}
        >
          <ChatWidgetTroubleshootingTool
            css={{
              container: styles.troubleshootContainer,
              textInput: styles.troubleshootInput,
              button: styles.troubleshootButton,
            }}
            promoter={promoter}
            buttonName="Verify"
            usedInSlideout
            isXRay
          />
        </CollapsibleContentContainerWithoutLocalState>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};

export const GetemailsSlideoutEditor = (
  props: GetemailsSlideoutEditorProps,
) => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);

  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const successCheckRef = useRef(
    activeFacebookPage?.onceSuccessfullyLoadedGlobalJs ||
      activeFacebookPage?.getemailsActivated,
  );

  const showSetupSlideout = !successCheckRef.current;

  useEffect(() => {
    if (
      result?.onceSuccessfullyLoadedGlobalJs &&
      !activeFacebookPage?.onceSuccessfullyLoadedGlobalJs &&
      activeFacebookPage?.id
    ) {
      connectedPageCRUDActions.retrievePage(activeFacebookPage?.id);
    }
  }, [result?.onceSuccessfullyLoadedGlobalJs]);

  if (showSetupSlideout) {
    return <GetemailsSetupSlideout {...props} />;
  }

  return <GetemailsSlideoutEditorSettings {...props} />;
};
